import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useWallet } from '../contexts/WalletContext'
import { useMyPageData } from '../hooks/useMyPage'
import type {
  MyPageParticipatingItem,
  MyPageCreatedItem,
  MyPageCompletedItem,
} from '../hooks/useMyPage'
import {
  CHALLENGE_TYPE,
  CHALLENGE_STATE,
  formatWeiToEth,
  getChallengeTypeName,
  getChallengeStatus,
  getChallengeStatusLabel,
  type ChallengeStatus,
} from '../types/challenge'

const SEPOLIA_ETHERSCAN = 'https://sepolia.etherscan.io'

function statusBadgeClass(status: ChallengeStatus) {
  switch (status) {
    case 'active':
      return 'badge badge-success'
    case 'ended':
      return 'badge badge-ink'
    case 'refunded':
      return 'badge badge-warning'
    case 'upcoming':
      return 'badge badge-neutral'
  }
}

function ParticipatingCard({ item }: { item: MyPageParticipatingItem }) {
  const status = getChallengeStatus(item)
  return (
    <div
      className="card card-pad"
      style={{ marginBottom: 10 }}
      data-testid={`participating-item-${item.address}`}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 8,
          marginBottom: 8,
          flexWrap: 'wrap',
        }}
      >
        <Link to={`/challenge/${item.address}`} style={{ fontWeight: 700, fontSize: 15 }}>
          {item.name}
        </Link>
        <div style={{ display: 'flex', gap: 6 }}>
          <span className={statusBadgeClass(status)}>{getChallengeStatusLabel(status)}</span>
          <span className="badge badge-primary">{getChallengeTypeName(item.challengeType)}</span>
        </div>
      </div>
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, flexWrap: 'wrap' }}
      >
        {/* Proof submission is only possible once the challenge is actually ACTIVE on-chain. */}
        {item.state === CHALLENGE_STATE.ACTIVE &&
          (item.todayProofDone ? (
            <span data-testid={`proof-done-${item.address}`} style={{ color: 'var(--success)' }}>
              ✓ 오늘 인증 완료
            </span>
          ) : (
            <>
              <span data-testid={`proof-undone-${item.address}`} style={{ color: 'var(--danger)' }}>
                ✗ 오늘 인증 미완료
              </span>
              <Link
                to={`/submit/${item.address}`}
                className="btn btn-primary btn-sm"
                data-testid={`submit-link-${item.address}`}
              >
                인증하기
              </Link>
            </>
          ))}
        {status === 'refunded' && (
          <Link
            to={`/challenge/${item.address}`}
            className="btn btn-primary btn-sm"
            data-testid={`refund-link-${item.address}`}
          >
            환불 받기 →
          </Link>
        )}
        <span style={{ color: 'var(--ink-3)', marginLeft: 'auto' }}>
          참가자 {item.participantCount}/{item.maxParticipants}명
        </span>
      </div>
    </div>
  )
}

function CreatedCard({ item }: { item: MyPageCreatedItem }) {
  const status = getChallengeStatus(item)
  return (
    <div
      className="card card-pad"
      style={{ marginBottom: 10 }}
      data-testid={`created-item-${item.address}`}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 8,
          marginBottom: 8,
          flexWrap: 'wrap',
        }}
      >
        <Link to={`/challenge/${item.address}`} style={{ fontWeight: 700, fontSize: 15 }}>
          {item.name}
        </Link>
        <span className={statusBadgeClass(status)}>{getChallengeStatusLabel(status)}</span>
      </div>
      <div style={{ fontSize: 13, color: 'var(--ink-2)' }}>
        참가자 {item.participantCount}/{item.maxParticipants}명
      </div>
      {item.pendingReportsCount > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
          <span className="badge badge-warning" data-testid={`report-badge-${item.address}`}>
            신고 {item.pendingReportsCount}건 · 관리자 검토 중
          </span>
        </div>
      )}
    </div>
  )
}

function CompletedCard({ item }: { item: MyPageCompletedItem }) {
  const isPromo =
    item.challengeType === CHALLENGE_TYPE.PROMO_TOKEN ||
    item.challengeType === CHALLENGE_TYPE.PROMO_PHYSICAL
  const resultBadge = !item.isCompleted
    ? { cls: 'badge badge-danger', label: '탈락' }
    : isPromo && item.isWinner
      ? { cls: 'badge badge-primary', label: '당첨 🎉' }
      : { cls: 'badge badge-success', label: '완주 ✓' }
  const showPayout = !isPromo && item.isCompleted && item.payoutPerWinner > 0n

  return (
    <div
      className="card card-pad"
      style={{ marginBottom: 10 }}
      data-testid={`completed-item-${item.address}`}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 8,
          marginBottom: 8,
          flexWrap: 'wrap',
        }}
      >
        <Link to={`/challenge/${item.address}`} style={{ fontWeight: 700, fontSize: 15 }}>
          {item.name}
        </Link>
        <span className={resultBadge.cls} data-testid={`completed-status-${item.address}`}>
          {resultBadge.label}
        </span>
      </div>
      <div
        style={{
          display: 'flex',
          gap: 12,
          fontSize: 13,
          color: 'var(--ink-2)',
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <span>{getChallengeTypeName(item.challengeType)}</span>
        {showPayout && (
          <span className="eth-amt">
            {item.isClaimed ? '수령 완료: ' : '수령 가능: '}
            {formatWeiToEth(item.payoutPerWinner)} ETH
          </span>
        )}
        {item.isCompleted && (
          <Link to={`/result/${item.address}`} className="btn btn-quiet btn-sm">
            결과 보기 →
          </Link>
        )}
      </div>
    </div>
  )
}

type TabId = 'participating' | 'created' | 'completed'

export default function MyPage() {
  const { address: userAddress, isConnected, openConnectModal } = useWallet()
  const [activeTab, setActiveTab] = useState<TabId>('participating')
  const { data, isLoading } = useMyPageData(userAddress)

  if (!isConnected) {
    return (
      <div className="empty" style={{ paddingTop: 60 }} data-testid="not-connected">
        <span className="empty-icon">👤</span>
        <p>지갑을 연결하면 내 챌린지 현황을 확인할 수 있습니다.</p>
        <button className="btn btn-primary" onClick={openConnectModal}>
          지갑 연결
        </button>
      </div>
    )
  }

  const participating = data?.participating ?? []
  const created = data?.created ?? []
  const completed = data?.completed ?? []

  return (
    <div style={{ maxWidth: 700, margin: '0 auto' }}>
      {/* Profile */}
      <div
        className="card card-pad"
        style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}
      >
        <div
          style={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            background: 'var(--primary-weak)',
            display: 'grid',
            placeItems: 'center',
            fontSize: 22,
            flexShrink: 0,
          }}
        >
          👤
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            className="mono"
            style={{ fontSize: 14, fontWeight: 600, wordBreak: 'break-all' }}
            data-testid="wallet-address"
          >
            {userAddress}
          </div>
          <a
            href={`${SEPOLIA_ETHERSCAN}/address/${userAddress ?? ''}`}
            target="_blank"
            rel="noreferrer"
            style={{ fontSize: 12, color: 'var(--primary)' }}
            data-testid="etherscan-link"
          >
            Etherscan ↗
          </a>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs">
        {[
          {
            id: 'participating' as TabId,
            label: `참가 중${participating.length > 0 ? ` (${participating.length})` : ''}`,
            testid: 'tab-participating',
          },
          {
            id: 'created' as TabId,
            label: `개설한 챌린지${created.length > 0 ? ` (${created.length})` : ''}`,
            testid: 'tab-created',
          },
          {
            id: 'completed' as TabId,
            label: `완료된 챌린지${completed.length > 0 ? ` (${completed.length})` : ''}`,
            testid: 'tab-completed',
          },
        ].map((t) => (
          <button
            key={t.id}
            className={`tab-btn${activeTab === t.id ? ' active' : ''}`}
            onClick={() => setActiveTab(t.id)}
            data-testid={t.testid}
          >
            {t.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="empty" data-testid="my-page-loading">
          <div className="spinner" />
          <p>불러오는 중…</p>
        </div>
      ) : (
        <>
          {activeTab === 'participating' && (
            <div data-testid="tab-content-participating">
              {participating.length === 0 ? (
                <div className="empty">
                  <p>참가 중인 챌린지가 없습니다.</p>
                </div>
              ) : (
                participating.map((item) => <ParticipatingCard key={item.address} item={item} />)
              )}
            </div>
          )}
          {activeTab === 'created' && (
            <div data-testid="tab-content-created">
              {created.length === 0 ? (
                <div className="empty">
                  <p>개설한 챌린지가 없습니다.</p>
                </div>
              ) : (
                created.map((item) => <CreatedCard key={item.address} item={item} />)
              )}
            </div>
          )}
          {activeTab === 'completed' && (
            <div data-testid="tab-content-completed">
              {completed.length === 0 ? (
                <div className="empty">
                  <p>완료된 챌린지가 없습니다.</p>
                </div>
              ) : (
                completed.map((item) => <CompletedCard key={item.address} item={item} />)
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}
